/**
 * Recorder
 *
 * @copyright 2018 Linguistica 360
 */

import { Script, injector } from '../shared/injector.js';

import $ from 'jquery';
import { $wrap } from '../shared/utils.js';
import DetectRTC from 'detectrtc';
import { EventEmitter } from '../shared/event-emitter.js';
import Modernizr from 'modernizr';
import clamp from 'lodash/clamp.js';
import iziToast from 'izitoast';

const audioContext = new AudioContext();

class Recorder {
    constructor(audioContext, bufferLen = 2048) {
        this.audioContext = audioContext;
        this.bufferLen = bufferLen;

        this.mediaRecorder = null;
        this.analyser = null;
        this.sourceNode = null;
        this.recordedBlob = null;

        this.recording = false;
        this.offset = null;
        this.duration = 0;
        this.visualInterval = null;

        this.events = new EventEmitter();
        this.maximum = 0.1;
    }

    init(stream) {
        this.mediaRecorder = new MediaRecorder(stream);
        this.mediaRecorder.ondataavailable = (e) => {
            this.recordedBlob = e.data;
        };

        const input = this.audioContext.createMediaStreamSource(stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = this.bufferLen;
        this.analyser.smoothingTimeConstant = 0.0;

        input.connect(this.analyser);
    }

    start() {
        if (!this.mediaRecorder) throw new Error('Recorder not initialized');

        this.recording = true;
        this.offset = null;
        this.startTime = this.audioContext.currentTime;
        this.mediaRecorder.start();

        this._startAmplitudeUpdates();
    }

    stop() {
        this.recording = false;
        this.duration = (new Date()).getTime() - this.startTime * 1000;
        this.mediaRecorder.stop();

        this._stopAmplitudeUpdates();
    }

    async play(onEnded = () => {}) {
        if (!this.recordedBlob) return;

        const arrayBuffer = await this.recordedBlob.arrayBuffer();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

        this.sourceNode?.disconnect();
        this.sourceNode = this.audioContext.createBufferSource();
        this.sourceNode.buffer = audioBuffer;
        this.sourceNode.connect(this.audioContext.destination);

        this.startTime = this.audioContext.currentTime;
        const offset = this.offset || 0;
        this.sourceNode.start(0, offset);

        this._startPlaybackProgress(audioBuffer.duration, offset);

        this.sourceNode.onended = () => {
            this.offset = null;
            this._stopPlaybackProgress();
            onEnded();
        };
    }

    pause() {
        if (!this.sourceNode) return;

        const elapsed = this.audioContext.currentTime - this.startTime;
        this.offset = (this.offset || 0) + elapsed;
        this.sourceNode.stop();
        this._stopPlaybackProgress();
    }

    getAmplitude() {
        const data = new Uint8Array(this.analyser.frequencyBinCount);
        this.analyser.getByteFrequencyData(data);

        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        if (this.maximum < avg * 1.5) this.maximum = avg * 1.5;

        return avg / this.maximum;
    }

    _startAmplitudeUpdates() {
        this._stopAmplitudeUpdates();
        this.visualInterval = setInterval(() => {
            this.events.emit('bar', this.getAmplitude(), false);
        }, 100);
    }

    _stopAmplitudeUpdates() {
        clearInterval(this.visualInterval);
    }

    _startPlaybackProgress(duration, offset = 0) {
        this._stopPlaybackProgress();
        this.visualInterval = setInterval(() => {
            const elapsed = this.audioContext.currentTime - this.startTime + offset;
            this.events.emit('bar', elapsed * 1000 / (duration * 1000));
        }, 100);
    }

    _stopPlaybackProgress() {
        clearInterval(this.visualInterval);
    }
}

// Fix iOS Audio Context by Blake Kus
// https://gist.github.com/kus/3f01d60569eeadefe3a1
// MIT license
function fixAudioContext() {
    if (audioContext) {
        // Create empty buffer
        const buffer = audioContext.createBuffer(1, 1, 22050);
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        // Connect to output (speakers)
        source.connect(audioContext.destination);
        // Play sound
        if (source.start) {
            source.start(0);
        } else if (source.play) {
            source.play(0);
        } else if (source.noteOn) {
            source.noteOn(0);
        }
    }

    // Remove events
    document.removeEventListener('touchstart', fixAudioContext);
    document.removeEventListener('touchend', fixAudioContext);
}

injector.injectClass(class extends Script {
    onLoad() {
        // iOS 6-8
        document.addEventListener('touchstart', fixAudioContext);
        // iOS 9
        document.addEventListener('touchend', fixAudioContext);

        this.recorder = new Recorder(audioContext);

        this.$record = $('body').find('.recorder-record')
            .on('click', this.wrap(this.record));

        this.$stop = $('body').find('.recorder-stop')
            .on('click', this.wrap(this.stop));

        this.$toggle = $('body').find('.recorder-toggle');
        this.$toggle.on('click', $wrap(($this) => {
            if ($this.is('.recorder-play')) this.play();
            else this.pause();
        }));

        const $handle = $('.recorder .handle');
        const $bar = $('.recorder .bar');

        this.recorder.events.on('bar', (progress, handle = true) => {
            progress = clamp(progress, 0, 1);

            if (handle) $handle.css('visibility', 'visible');
            else $handle.css('visibility', 'hidden');

            $bar.css('width', (progress * 100) + '%');

            const width = $bar.css('width');
            const offset = $bar.parent().offset().left + parseFloat(width);

            $handle.offset({
                top: $bar.parent().offset().top - $handle.height() / 4,
                left: offset,
            });
        });
    }

    record() {
        if (!DetectRTC.isWebRTCSupported) {
            iziToast.error({
                title: 'Browser Unsupported',
                message: 'Your browser does not support WebRTC',
            });
        }

        else if (!Modernizr.getusermedia) {
            iziToast.error({
                title: 'Browser Unsupported',
                message: 'Your browser does not support UserMedia',
            });
        }

        else if (!this.hasPermissions()) {
            this.requestPermissions(() => this._record());
        }

        else this._record();
    }

    _record() {
        this.recorder.start();

        this.$record.css('display', 'none');
        this.$toggle.css('display', 'none');

        this.$stop.css('display', 'inline-block');
    }

    stop() {
        this.recorder.stop();

        this.$stop.css('display', 'none');

        this.$record.css('display', 'inline-block');
        this.$toggle.css('display', 'inline-block');
    }

    play() {
        this.recorder.play(() => {
            this.$toggle.addClass('recorder-play')
                .removeClass('recorder-pause');
        });

        this.$toggle.removeClass('recorder-play').addClass('recorder-pause');
    }

    pause() {
        this.recorder.pause();

        this.$toggle.removeClass('recorder-pause').addClass('recorder-play');
    }

    hasMicrophone() {
        return DetectRTC.hasMicrophone;
    }

    hasPermissions() {
        return DetectRTC.isWebsiteHasMicrophonePermissions;
    }

    requestPermissions(success) {
        if (this.recorder.mediaRecorder) return success(); // make sure we do not re-init

        navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
            this.recorder.init(stream);

            success();
        }).catch((e) => {
            console.error(e);
            iziToast.error({
                title: 'No Microphone Access',
                message: 'Permission to access microphone not granted',
            });
        });
    }
});
